import * as THREE from 'three';
import { islandBase, displaceGeometry, pedestal, MAT } from './helpers.js';

// ============================================================================
// One factory per topic. Multi-project topics support a `variant` index
// (cycled per project in main.js) to vary silhouettes within a single category.
// ============================================================================

// ---------- QUANT (4 variants) ----------
// 0: chart-bar obelisks  1: stepped ziggurat  2: lighthouse  3: observatory dome
export function quantIsland(color, seed, variant = 0) {
  const g = islandBase(18, 14, seed);
  g.add(pedestal(5.5, 1.6));
  const mat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.55, roughness: 0.35, metalness: 0.5
  });
  const accent = new THREE.MeshBasicMaterial({ color });
  const v = variant % 4;

  if (v === 0) {
    const heights = [12, 22, 16];
    for (let i = 0; i < heights.length; i++) {
      const h = heights[i];
      const bar = new THREE.Mesh(new THREE.BoxGeometry(2.4, h, 2.4), mat);
      bar.position.set((i - 1) * 4.8, 4.5 + h / 2, 0);
      g.add(bar);
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.9, 12, 8), accent);
      cap.position.set(bar.position.x, 4.5 + h + 1.2, 0);
      g.add(cap);
    }
  } else if (v === 1) {
    // Stepped ziggurat
    const tiers = [{w: 10, h: 3.6}, {w: 7.6, h: 3}, {w: 5.4, h: 2.6}, {w: 3.4, h: 2.2}];
    let y = 4.6;
    tiers.forEach(t => {
      const box = new THREE.Mesh(new THREE.BoxGeometry(t.w, t.h, t.w), mat);
      box.position.y = y + t.h / 2;
      g.add(box);
      y += t.h;
    });
    const crown = new THREE.Mesh(new THREE.OctahedronGeometry(1.6, 0), accent);
    crown.position.y = y + 2.2;
    crown.rotation.y = Math.PI / 4;
    g.add(crown);
  } else if (v === 2) {
    // Lighthouse
    const tower = new THREE.Mesh(new THREE.CylinderGeometry(1.8, 2.6, 22, 10), mat);
    tower.position.y = 4.5 + 11;
    g.add(tower);
    const ringBase = new THREE.Mesh(new THREE.CylinderGeometry(2.4, 2.6, 0.6, 12), mat);
    ringBase.position.y = 4.5 + 22.3;
    g.add(ringBase);
    const beacon = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.2, 2.8, 12), accent);
    beacon.position.y = 4.5 + 24;
    g.add(beacon);
    g.userData.beacon = beacon;
    const dome = new THREE.Mesh(new THREE.ConeGeometry(2.6, 3, 10), mat);
    dome.position.y = 4.5 + 26.8;
    g.add(dome);
  } else {
    // Observatory dome
    const base = new THREE.Mesh(new THREE.CylinderGeometry(6.5, 6.5, 8, 14), mat);
    base.position.y = 4.5 + 4;
    g.add(base);
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(6.5, 18, 12, 0, Math.PI * 2, 0, Math.PI / 2),
      mat
    );
    dome.position.y = 4.5 + 8;
    g.add(dome);
    const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 0.7, 9, 8), accent);
    tube.position.set(0, 4.5 + 10, 4.2);
    tube.rotation.x = -Math.PI / 3;
    g.add(tube);
  }
  return g;
}

// ---------- AI / ML (4 variants) ----------
// 0: neural tree  1: brain orb with orbiting smaller orbs  2: transformer stack  3: antenna tower
export function aiIsland(color, seed, variant = 0) {
  const g = islandBase(15, 14, seed);
  g.add(pedestal(4.2, 1.6));
  const stdMat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.5, roughness: 0.35
  });
  const accent = new THREE.MeshBasicMaterial({ color });
  const lineMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.55 });
  const v = variant % 4;

  if (v === 0) {
    // Neural tree
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1.0, 16, 6), stdMat);
    trunk.position.y = 4.5 + 8;
    g.add(trunk);
    const positions = [[0, 25, 0], [-5, 19, 2], [5, 19, -2], [-2, 22, -4], [3, 22, 4]];
    const nodes = positions.map(p => {
      const n = new THREE.Mesh(new THREE.SphereGeometry(1.6, 14, 10), accent);
      n.position.set(...p);
      g.add(n);
      return n;
    });
    for (let i = 1; i < nodes.length; i++) {
      const geo = new THREE.BufferGeometry().setFromPoints([nodes[0].position, nodes[i].position]);
      g.add(new THREE.Line(geo, lineMat));
    }
  } else if (v === 1) {
    // Brain orb + orbiting smaller orbs
    const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 1.2, 10, 6), stdMat);
    pillar.position.y = 4.5 + 5;
    g.add(pillar);
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(4.5, 1), new THREE.MeshStandardMaterial({
      color, emissive: color, emissiveIntensity: 0.9, roughness: 0.2, metalness: 0.4
    }));
    core.position.y = 4.5 + 16;
    g.add(core);
    g.userData.aiCore = core;
    // Orbiters
    const orbiters = [];
    for (let i = 0; i < 4; i++) {
      const o = new THREE.Mesh(new THREE.SphereGeometry(0.9, 12, 8), accent);
      o.position.y = 4.5 + 16;
      o.userData = { angle: (i / 4) * Math.PI * 2, radius: 6.5 + (i % 2) * 1.5, speed: 0.6 + i * 0.15 };
      g.add(o);
      orbiters.push(o);
    }
    g.userData.aiOrbiters = orbiters;
  } else if (v === 2) {
    // Transformer stack — horizontal disc layers
    const layers = 5;
    for (let i = 0; i < layers; i++) {
      const disc = new THREE.Mesh(
        new THREE.CylinderGeometry(5 - i * 0.4, 5 - i * 0.4, 1.4, 12),
        stdMat
      );
      disc.position.y = 4.5 + 2 + i * 3.2;
      g.add(disc);
      // Edge glow ring
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(5 - i * 0.4, 0.18, 6, 18),
        accent
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 4.5 + 2 + i * 3.2 + 0.7;
      g.add(ring);
    }
  } else {
    // Antenna tower
    const tower = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.8, 22, 6), stdMat);
    tower.position.y = 4.5 + 11;
    g.add(tower);
    // Crossarms with dot at each end
    [8, 14, 19].forEach((y, idx) => {
      const arm = new THREE.Mesh(new THREE.BoxGeometry(7 - idx * 1.5, 0.3, 0.3), stdMat);
      arm.position.y = 4.5 + y;
      g.add(arm);
      const span = (7 - idx * 1.5) / 2;
      [-span, span].forEach(x => {
        const dot = new THREE.Mesh(new THREE.SphereGeometry(0.6, 8, 6), accent);
        dot.position.set(x, 4.5 + y, 0);
        g.add(dot);
      });
    });
    // Tip beacon
    const tip = new THREE.Mesh(new THREE.SphereGeometry(1.0, 12, 8), accent);
    tip.position.y = 4.5 + 23;
    g.add(tip);
  }
  return g;
}

// ---------- QUANTUM (single — keep the floating crystal shrine) ----------
export function quantumIsland(color) {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 1.3,
    roughness: 0.15, metalness: 0.9, transparent: true, opacity: 0.94
  });
  const ico = new THREE.Mesh(new THREE.IcosahedronGeometry(22, 1), mat);
  g.add(ico);
  g.userData.spin = ico;

  const shellMat = new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: 0.28 });
  const shell = new THREE.Mesh(new THREE.IcosahedronGeometry(32, 1), shellMat);
  g.add(shell);
  g.userData.shell = shell;

  const outerMat = new THREE.MeshBasicMaterial({ color, wireframe: true, transparent: true, opacity: 0.1 });
  const outer = new THREE.Mesh(new THREE.IcosahedronGeometry(46, 1), outerMat);
  g.add(outer);
  g.userData.outer = outer;

  const ringMat = new THREE.PointsMaterial({ color, size: 1.6, transparent: true, opacity: 0.55 });
  const ringPts = [];
  for (let i = 0; i < 120; i++) {
    const a = Math.random() * Math.PI * 2;
    const r = 38 + Math.random() * 14;
    ringPts.push(new THREE.Vector3(Math.cos(a) * r, (Math.random() - 0.5) * 5, Math.sin(a) * r));
  }
  const ringGeo = new THREE.BufferGeometry().setFromPoints(ringPts);
  g.add(new THREE.Points(ringGeo, ringMat));
  return g;
}

// ---------- COGNITIVE (2 variants) ----------
// 0: branching tree  1: consciousness orb cluster
export function cognitiveIsland(color, seed, variant = 0) {
  const g = islandBase(14, 14, seed);
  g.add(pedestal(3.4, 1.6));
  const branchMat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.6, roughness: 0.35
  });
  const accent = new THREE.MeshBasicMaterial({ color });
  const v = variant % 2;

  if (v === 0) {
    function branch(parent, len, rad, depth) {
      if (depth === 0) return;
      const cyl = new THREE.Mesh(
        new THREE.CylinderGeometry(rad * 0.6, rad, len, 6),
        branchMat
      );
      cyl.position.y = len / 2;
      parent.add(cyl);
      if (depth > 1) {
        [0.55, -0.55].forEach(angle => {
          const sub = new THREE.Group();
          sub.position.y = len;
          sub.rotation.z = angle + (Math.random() - 0.5) * 0.4;
          sub.rotation.y = Math.random() * Math.PI * 2;
          parent.add(sub);
          branch(sub, len * 0.72, rad * 0.7, depth - 1);
        });
      }
    }
    const trunk = new THREE.Group();
    trunk.position.y = 4.5;
    g.add(trunk);
    branch(trunk, 11, 1.5, 4);
  } else {
    // Consciousness orb cluster — central sphere with smaller satellites
    const core = new THREE.Mesh(new THREE.SphereGeometry(5, 16, 12), branchMat);
    core.position.y = 4.5 + 11;
    g.add(core);
    const sats = [[6, 14, 0], [-5, 13, 3], [3, 18, -4], [-2, 19, 4], [4, 9, 3]];
    sats.forEach(p => {
      const sat = new THREE.Mesh(new THREE.SphereGeometry(1.6 + Math.random(), 12, 8), accent);
      sat.position.set(...p);
      g.add(sat);
    });
    // arcs of light
    const arcMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.5 });
    sats.forEach(p => {
      const geo = new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(0, 4.5 + 11, 0),
        new THREE.Vector3(...p)
      ]);
      g.add(new THREE.Line(geo, arcMat));
    });
  }
  return g;
}

// ---------- CV (2 variants) ----------
// 0: wireframe detection boxes  1: watchtower with rotating eye
export function cvIsland(color, seed, variant = 0) {
  const g = islandBase(16, 10, seed);
  g.add(pedestal(4.8, 1.6));
  const wireMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, wireframe: true });
  const fillMat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.55, transparent: true, opacity: 0.22
  });
  const accent = new THREE.MeshBasicMaterial({ color });
  const v = variant % 2;

  if (v === 0) {
    for (let i = 0; i < 4; i++) {
      const w = 5 + Math.random() * 6;
      const h = 4 + Math.random() * 7;
      const d = 5 + Math.random() * 6;
      const x = (Math.random() - 0.5) * 18;
      const y = 5 + Math.random() * 18;
      const z = (Math.random() - 0.5) * 18;
      const box = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wireMat);
      box.position.set(x, y, z);
      g.add(box);
      const fill = new THREE.Mesh(new THREE.BoxGeometry(w * 0.94, h * 0.94, d * 0.94), fillMat);
      fill.position.set(x, y, z);
      g.add(fill);
    }
  } else {
    // Watchtower
    const baseMat = new THREE.MeshStandardMaterial({
      color, emissive: color, emissiveIntensity: 0.45, roughness: 0.4, metalness: 0.4
    });
    const tower = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 3.2, 18, 8), baseMat);
    tower.position.y = 4.5 + 9;
    g.add(tower);
    // Cabin (square)
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(7, 4, 7), baseMat);
    cabin.position.y = 4.5 + 20;
    g.add(cabin);
    // Eye — rotating accent
    const eye = new THREE.Mesh(new THREE.SphereGeometry(1.4, 14, 10), accent);
    eye.position.set(3.6, 4.5 + 20, 0);
    g.add(eye);
    g.userData.cvEye = eye;
    g.userData.cvEyeCenter = new THREE.Vector3(0, 4.5 + 20, 0);
    // Roof
    const roof = new THREE.Mesh(new THREE.ConeGeometry(5.5, 3, 4), baseMat);
    roof.rotation.y = Math.PI / 4;
    roof.position.y = 4.5 + 23.5;
    g.add(roof);
  }
  return g;
}

// ---------- PLATFORM (3 variants) ----------
// 0: tiered pagoda  1: tall lighthouse  2: modular city block
export function platformIsland(color, seed, variant = 0) {
  const g = islandBase(16, 14, seed);
  g.add(pedestal(5.5, 1.6));
  const mat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.45,
    roughness: 0.35, metalness: 0.6
  });
  const dotMat = new THREE.MeshBasicMaterial({ color });
  const v = variant % 3;

  if (v === 0) {
    const tiers = [{w: 8, h: 3.0}, {w: 6, h: 2.5}, {w: 4, h: 2.0}];
    let y = 4.6;
    tiers.forEach(t => {
      const box = new THREE.Mesh(new THREE.BoxGeometry(t.w, t.h, t.w), mat);
      box.position.y = y + t.h / 2;
      g.add(box);
      const roof = new THREE.Mesh(new THREE.ConeGeometry(t.w * 0.75, 1.2, 4), mat);
      roof.rotation.y = Math.PI / 4;
      roof.position.y = y + t.h + 0.6;
      g.add(roof);
      for (let s = -1; s <= 1; s += 2) {
        const dot = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 6), dotMat);
        dot.position.set(s * (t.w / 2 + 0.05), y + t.h / 2, 0);
        g.add(dot);
      }
      y += t.h + 1.2;
    });
  } else if (v === 1) {
    // Stocky round lighthouse
    const tower = new THREE.Mesh(new THREE.CylinderGeometry(3, 4, 18, 12), mat);
    tower.position.y = 4.5 + 9;
    g.add(tower);
    const ring = new THREE.Mesh(new THREE.CylinderGeometry(3.6, 3.8, 1, 12), mat);
    ring.position.y = 4.5 + 18.5;
    g.add(ring);
    const lamp = new THREE.Mesh(new THREE.CylinderGeometry(3.2, 3.2, 3, 12), dotMat);
    lamp.position.y = 4.5 + 20.5;
    g.add(lamp);
    g.userData.beacon = lamp;
    const cap = new THREE.Mesh(new THREE.ConeGeometry(3.6, 3, 12), mat);
    cap.position.y = 4.5 + 23.5;
    g.add(cap);
    // Window dots on tower
    for (let i = 0; i < 4; i++) {
      const dot = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 6), dotMat);
      const a = (i / 4) * Math.PI * 2;
      dot.position.set(Math.cos(a) * 3.1, 4.5 + 11 + (i % 2) * 4, Math.sin(a) * 3.1);
      g.add(dot);
    }
  } else {
    // Modular city block
    const buildings = [
      { x: -5, z: -3, w: 4, h: 14, d: 4 },
      { x: 4, z: -4, w: 4.5, h: 18, d: 4.5 },
      { x: -3, z: 5, w: 5, h: 11, d: 5 },
      { x: 5, z: 4, w: 3.5, h: 22, d: 3.5 }
    ];
    buildings.forEach(b => {
      const box = new THREE.Mesh(new THREE.BoxGeometry(b.w, b.h, b.d), mat);
      box.position.set(b.x, 4.5 + b.h / 2, b.z);
      g.add(box);
      // antenna on top
      const ant = new THREE.Mesh(new THREE.SphereGeometry(0.45, 8, 6), dotMat);
      ant.position.set(b.x, 4.5 + b.h + 0.5, b.z);
      g.add(ant);
    });
  }
  return g;
}

// ---------- AUDIO (single) ----------
export function audioIsland(color, seed) {
  const g = islandBase(16, 14, seed);
  g.add(pedestal(5, 1.6));
  const mat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.7, roughness: 0.25, metalness: 0.4
  });
  const bars = [];
  for (let ring = 0; ring < 3; ring++) {
    const r = 5 + ring * 3.5;
    const count = 10 + ring * 5;
    const heightBase = 4 + ring * 3;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const h = heightBase + Math.random() * 6;
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.8, h, 0.8), mat);
      bar.position.set(Math.cos(a) * r, 4.5 + h / 2, Math.sin(a) * r);
      bar.userData = {
        baseHeight: h,
        phase: Math.random() * Math.PI * 2,
        freq: 1.8 + ring * 0.6
      };
      g.add(bar);
      bars.push(bar);
    }
  }
  g.userData.bars = bars;
  return g;
}

// ---------- VIZ (single) ----------
export function vizIsland(color, seed) {
  const g = islandBase(15, 14, seed);
  g.add(pedestal(4.5, 1.6));
  const palette = [0xff7090, 0x70d0ff, 0xffc870, 0x90e0a0, 0xb090ff];
  for (let i = 0; i < 5; i++) {
    const mat = new THREE.MeshStandardMaterial({
      color: palette[i], emissive: palette[i], emissiveIntensity: 0.5,
      transparent: true, opacity: 0.8, side: THREE.DoubleSide
    });
    const w = 13 - i * 0.6;
    const h = 9 - i * 0.4;
    const plane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
    plane.position.set(0, 4.5 + 5 + i * 4, 0);
    plane.rotation.y = i * 0.65;
    plane.rotation.x = -0.18;
    g.add(plane);
  }
  return g;
}

// ---------- SIM (single) ----------
export function simIsland(color, seed) {
  const g = islandBase(14, 14, seed);
  g.add(pedestal(4, 1.6));
  const pillarMat = new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.45, roughness: 0.4, metalness: 0.5
  });
  const pillar = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.4, 22, 8), pillarMat);
  pillar.position.y = 4.5 + 12;
  g.add(pillar);
  const orbitMat = new THREE.MeshBasicMaterial({ color });
  const orbits = [];
  for (let ring = 0; ring < 3; ring++) {
    const r = 6 + ring * 4;
    const heightOffset = 4.5 + 5 + ring * 5;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const ball = new THREE.Mesh(new THREE.SphereGeometry(1.2, 12, 8), orbitMat);
      ball.position.set(Math.cos(a) * r, heightOffset, Math.sin(a) * r);
      ball.userData = {
        angle: a, radius: r, height: heightOffset,
        speed: 0.5 - ring * 0.12, dir: ring % 2 === 0 ? 1 : -1
      };
      g.add(ball);
      orbits.push(ball);
    }
  }
  g.userData.orbits = orbits;
  return g;
}

// ---------- MISC ----------
export function miscIsland(color, seed) {
  const g = islandBase(12, 10, seed);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.65 });
  for (let i = 0; i < 4; i++) {
    const s = 2 + Math.random() * 4;
    const r = new THREE.Mesh(new THREE.DodecahedronGeometry(s, 0), mat);
    r.position.set((Math.random() - 0.5) * 14, 2 + s, (Math.random() - 0.5) * 14);
    r.rotation.set(Math.random(), Math.random(), Math.random());
    g.add(r);
  }
  return g;
}

// ---------- PROFILE (Ilia's cottage — central, bigger, actual house) ----------
export function profileIsland(color, seed) {
  // Bigger island so the house has room to breathe
  const g = islandBase(34, 24, seed);
  g.add(pedestal(13, 2.8));

  // Materials — Ghibli warm cottage palette
  const wallMat       = new THREE.MeshStandardMaterial({ color: 0xeacf9a, roughness: 0.7 });
  const wallTrimMat   = new THREE.MeshStandardMaterial({ color: 0xa67a48, roughness: 0.6 });
  const roofMat       = new THREE.MeshStandardMaterial({ color: 0x7a4530, roughness: 0.55 });
  const doorMat       = new THREE.MeshStandardMaterial({ color: 0x4a2c1a, roughness: 0.7 });
  const windowMat     = new THREE.MeshBasicMaterial({ color: 0xfff0c0 });
  const stoneMat      = new THREE.MeshStandardMaterial({ color: 0xa89880, roughness: 0.85 });
  const pathStoneMat  = new THREE.MeshStandardMaterial({ color: 0xb0a288, roughness: 0.85 });
  const goldMat       = new THREE.MeshStandardMaterial({
    color: 0xf4c870, emissive: 0xf4c870, emissiveIntensity: 0.5,
    roughness: 0.25, metalness: 0.85
  });

  // Top of pedestal = approximate ground level for the house
  const G = 5.6;

  // ---- Main building ----
  const mainW = 14, mainH = 10, mainD = 12;
  const main = new THREE.Mesh(new THREE.BoxGeometry(mainW, mainH, mainD), wallMat);
  main.position.set(0, G + mainH / 2, 0);
  g.add(main);

  // Wooden trim band along the top of the walls
  const trimBand = new THREE.Mesh(
    new THREE.BoxGeometry(mainW + 0.3, 0.7, mainD + 0.3),
    wallTrimMat
  );
  trimBand.position.set(0, G + mainH - 0.35, 0);
  g.add(trimBand);

  // Pitched 4-sided roof
  const roof = new THREE.Mesh(new THREE.ConeGeometry(mainW * 0.78, 7, 4), roofMat);
  roof.position.set(0, G + mainH + 3.5, 0);
  roof.rotation.y = Math.PI / 4;
  g.add(roof);

  // ---- Door + step + path ----
  const door = new THREE.Mesh(new THREE.BoxGeometry(2.6, 4.4, 0.4), doorMat);
  door.position.set(0, G + 2.2, mainD / 2 + 0.2);
  g.add(door);
  const doorKnob = new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 6), goldMat);
  doorKnob.position.set(0.85, G + 2.4, mainD / 2 + 0.45);
  g.add(doorKnob);
  const step = new THREE.Mesh(new THREE.BoxGeometry(4, 0.6, 1.4), stoneMat);
  step.position.set(0, G + 0.3, mainD / 2 + 0.8);
  g.add(step);
  // Path of round stones leading away from the door
  for (let i = 0; i < 5; i++) {
    const s = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.0, 0.3, 6), pathStoneMat);
    s.position.set(((i % 2) - 0.5) * 1.6, G + 0.15, mainD / 2 + 2.5 + i * 2.2);
    g.add(s);
  }

  // ---- Windows (lit, all four sides) ----
  function lit(w, h, d, x, y, z) {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(w + 0.4, h + 0.4, d), wallTrimMat);
    frame.position.set(x, y, z);
    g.add(frame);
    const pane = new THREE.Mesh(new THREE.BoxGeometry(w, h, d + 0.05), windowMat);
    pane.position.set(x, y, z);
    g.add(pane);
  }
  const winY = G + 6.2;
  const ws = 1.7;
  // Front
  lit(ws, ws, 0.3, -4.2, winY,  mainD / 2 + 0.15);
  lit(ws, ws, 0.3,  4.2, winY,  mainD / 2 + 0.15);
  // Back
  lit(ws, ws, 0.3, -3.2, winY, -mainD / 2 - 0.15);
  lit(ws, ws, 0.3,  3.2, winY, -mainD / 2 - 0.15);
  // Sides — different orientation, so reuse with rotated dims
  function litSide(x) {
    const frame = new THREE.Mesh(new THREE.BoxGeometry(0.3, ws + 0.4, ws + 0.4), wallTrimMat);
    frame.position.set(x, winY, 0);
    g.add(frame);
    const pane = new THREE.Mesh(new THREE.BoxGeometry(0.35, ws, ws), windowMat);
    pane.position.set(x, winY, 0);
    g.add(pane);
  }
  litSide(-mainW / 2 - 0.15);
  litSide( mainW / 2 + 0.15);

  // ---- Attached round tower with conical roof ----
  const towerR = 3.2;
  const towerH = 14;
  const towerX = -mainW / 2 - towerR + 1.4;
  const tower = new THREE.Mesh(new THREE.CylinderGeometry(towerR, towerR, towerH, 14), wallMat);
  tower.position.set(towerX, G + towerH / 2, 0);
  g.add(tower);
  const towerRoof = new THREE.Mesh(new THREE.ConeGeometry(towerR * 1.15, 6, 14), roofMat);
  towerRoof.position.set(towerX, G + towerH + 3, 0);
  g.add(towerRoof);
  // Tower window — narrow vertical slit
  const towerWin = new THREE.Mesh(new THREE.BoxGeometry(0.35, 2.4, 1.4), windowMat);
  towerWin.position.set(towerX - towerR - 0.05, G + 8, 0);
  g.add(towerWin);
  // Gold finial on top of tower roof
  const finialBall = new THREE.Mesh(new THREE.SphereGeometry(0.55, 10, 8), goldMat);
  finialBall.position.set(towerX, G + towerH + 6.2, 0);
  g.add(finialBall);
  const finialSpike = new THREE.Mesh(new THREE.ConeGeometry(0.18, 1.4, 6), goldMat);
  finialSpike.position.set(towerX, G + towerH + 7.4, 0);
  g.add(finialSpike);

  // ---- Chimney with cap ----
  const chimney = new THREE.Mesh(new THREE.BoxGeometry(1.8, 5, 1.8), stoneMat);
  chimney.position.set(3.2, G + mainH + 2, -2.6);
  g.add(chimney);
  const chimCap = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.6, 2.2), stoneMat);
  chimCap.position.set(3.2, G + mainH + 4.8, -2.6);
  g.add(chimCap);

  // ---- Weather vane on the main roof apex ----
  const vanePole = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 3, 6), goldMat);
  vanePole.position.set(0, G + mainH + 8.5, 0);
  g.add(vanePole);
  const vaneArrow = new THREE.Mesh(new THREE.ConeGeometry(0.5, 1.8, 6), goldMat);
  vaneArrow.position.set(0, G + mainH + 10.4, 0);
  vaneArrow.rotation.x = Math.PI / 2;
  g.add(vaneArrow);
  g.userData.weatherVane = vaneArrow;

  // ---- Magic signature: single thin gold ring slowly floating above ----
  const ringMat = new THREE.MeshBasicMaterial({
    color: 0xf4c870, transparent: true, opacity: 0.55, side: THREE.DoubleSide
  });
  const magicRing = new THREE.Mesh(
    new THREE.TorusGeometry(7, 0.3, 8, 40),
    ringMat
  );
  magicRing.position.y = G + mainH + 18;
  magicRing.rotation.x = Math.PI / 2 - 0.25;
  g.add(magicRing);
  g.userData.magicRing = magicRing;

  // ---- Small garden details — a couple of low bushes near the path ----
  const bushMat = new THREE.MeshStandardMaterial({ color: 0x6a8a3e, roughness: 0.9 });
  for (let i = 0; i < 4; i++) {
    const bush = new THREE.Mesh(new THREE.IcosahedronGeometry(1.2 + Math.random() * 0.5, 0), bushMat);
    const angle = i * (Math.PI / 2) + Math.PI / 4;
    bush.position.set(
      Math.cos(angle) * (mainW / 2 + 3.5),
      G + 1.0,
      Math.sin(angle) * (mainD / 2 + 3.5)
    );
    g.add(bush);
  }

  return g;
}

// Featured-treatment halo
export function applyFeaturedTreatment(group, color) {
  const haloMat = new THREE.MeshBasicMaterial({
    color, transparent: true, opacity: 0.16, depthWrite: false, side: THREE.DoubleSide
  });
  const halo = new THREE.Mesh(new THREE.RingGeometry(22, 36, 32), haloMat);
  halo.rotation.x = -Math.PI / 2;
  halo.position.y = -1;
  group.add(halo);
  group.userData.halo = halo;
}

export const FACTORIES = {
  profile: profileIsland,
  quant: quantIsland,
  ai: aiIsland,
  quantum: quantumIsland,
  cognitive: cognitiveIsland,
  cv: cvIsland,
  platform: platformIsland,
  audio: audioIsland,
  viz: vizIsland,
  sim: simIsland,
  misc: miscIsland
};
